import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { exploreAPI, creatorAPI, betCodeAPI } from '../../api/endpoints';
import { formatXPUNT, formatTimeAgo, formatDate, getUserId } from '../../utils/helpers';
import { 
  FaArrowLeft, FaUser, FaUsers, FaLock, FaCheck,
  FaTicketAlt, FaStar, FaCalendar, FaCopy, FaShare,
  FaTwitter, FaTelegram, FaWhatsapp, FaEnvelope,
  FaCrown, FaRocket, FaChartLine, FaDollarSign
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';
import './CreatorProfile.css';

const CreatorProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [creator, setCreator] = useState(null);
  const [privateBets, setPrivateBets] = useState([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionEnd, setSubscriptionEnd] = useState(null);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [creatorStats, setCreatorStats] = useState({
    totalBets: 0,
    winRate: 0,
    totalEarnings: 0,
  });

  useEffect(() => {
    if (!id) {
      navigate('/explore');
      return;
    }
    fetchCreatorData();
  }, [id]);

  const fetchCreatorData = async () => {
    setLoading(true);
    try {
      // Since we don't have a single creator endpoint,
      // we fetch all creators and find the one we need
      const response = await exploreAPI.getCreators();
      const found = response.data.find(c => c.id === parseInt(id));
      
      if (found) {
        setCreator(found);
      } else {
        // Try to get creator info directly
        try {
          const creatorInfoRes = await creatorAPI.getCreatorStatus(id);
          if (creatorInfoRes.data) {
            setCreator({
              id: parseInt(id),
              display_name: user?.display_name || `Creator #${id}`,
              ...creatorInfoRes.data,
            });
          }
        } catch (e) {
          console.log('Creator not found:', e);
        }
      }

      // Fetch creator stats
      try {
        const statsRes = await creatorAPI.getCreatorStats(id);
        if (statsRes.data) {
          setCreatorStats(statsRes.data);
        }
      } catch (e) {
        console.log('No stats available:', e);
      }

      // Check subscription status
      if (isAuthenticated) {
        try {
          const statusRes = await exploreAPI.getSubscriptionStatus(id);
          setIsSubscribed(statusRes.data.is_subscribed || false);
          setSubscriptionEnd(statusRes.data.end_date || null);
          setSubscriberCount(statusRes.data.subscribers_count || 0);
        } catch (e) {
          console.log('Subscription status not available:', e);
        }

        // Fetch private bets if subscribed
        if (isSubscribed) {
          try {
            const betsRes = await creatorAPI.getPrivateBets(id, { per_page: 20 });
            setPrivateBets(betsRes.data.data || []);
          } catch (e) {
            console.log('No private bets:', e);
            setPrivateBets([]);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch creator:', error);
      toast.error('Failed to load creator profile');
      navigate('/explore');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to subscribe');
      navigate('/login');
      return;
    }

    try {
      const response = await creatorAPI.subscribe(id);
      if (response.data && response.data.success) {
        setIsSubscribed(true);
        toast.success(response.data.message || 'Subscribed successfully!');
        fetchCreatorData();
      } else {
        toast.error(response.data?.message || 'Failed to subscribe');
      }
    } catch (error) {
      console.error('Subscribe error:', error);
      toast.error(error.response?.data?.message || 'Failed to subscribe');
    }
  };

  const handleUnsubscribe = async () => {
    try {
      const response = await creatorAPI.unsubscribe(id);
      if (response.data && response.data.success) {
        setIsSubscribed(false);
        toast.success(response.data.message || 'Unsubscribed successfully');
        fetchCreatorData();
      } else {
        toast.error(response.data?.message || 'Failed to unsubscribe');
      }
    } catch (error) {
      console.error('Unsubscribe error:', error);
      toast.error(error.response?.data?.message || 'Failed to unsubscribe');
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: `${creator?.display_name || 'Creator'} on XPunters`,
        text: `Check out ${creator?.display_name || 'this creator'} on XPunters!`,
        url: url,
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleShareSocial = (platform) => {
    const url = window.location.href;
    const text = `🎯 Check out ${creator?.display_name || 'this creator'} on XPunters! Subscribe for exclusive bet codes.`;
    const encodedText = encodeURIComponent(text);
    const encodedUrl = encodeURIComponent(url);
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      whatsapp: `https://wa.me/?text=${encodedText} ${encodedUrl}`,
      telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
      email: `mailto:?subject=Check out this creator on XPunters&body=${encodedText} ${encodedUrl}`,
    };

    window.open(shareUrls[platform], '_blank');
  };

  if (loading) {
    return (
      <div className="creator-profile-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="creator-not-found">
        <h2>Creator not found</h2>
        <p>The creator you're looking for doesn't exist or has been removed.</p>
        <Link to="/explore" className="btn btn-primary">
          <FaArrowLeft /> Back to Explore
        </Link>
      </div>
    );
  }

  const isOwnProfile = user && user.id === creator.id;

  return (
    <div className="creator-profile-page">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      <div className="creator-profile-card">
        {/* Profile Header */}
        <div className="profile-header">
          <div className="profile-avatar-wrapper">
            <div className="profile-avatar-large">
              {creator.display_name?.[0] || 'C'}
            </div>
            {creator.is_verified && (
              <span className="verified-badge-large">✓</span>
            )}
          </div>
          <div className="profile-info">
            <h1 className="profile-name">
              {creator.display_name || `Creator #${creator.id}`}
              {creator.is_verified && <FaCheck className="verified-icon" />}
            </h1>
            <p className="profile-bio">{creator.bio || 'Sports prediction creator'}</p>
            <div className="profile-meta">
              <div className="meta-item">
                <FaUsers className="meta-icon" />
                <span>{subscriberCount || creator.total_subscribers || 0} subscribers</span>
              </div>
              <div className="meta-item">
                <FaTicketAlt className="meta-icon" />
                <span>{creatorStats.totalBets || 0} bets</span>
              </div>
              <div className="meta-item">
                <FaChartLine className="meta-icon" />
                <span>{creatorStats.winRate || 0}% win rate</span>
              </div>
              <div className="meta-item">
                <FaDollarSign className="meta-icon" />
                <span>{formatXPUNT(creatorStats.totalEarnings || 0)} earned</span>
              </div>
            </div>
          </div>
          <div className="profile-actions">
            {isOwnProfile ? (
              <Link to="/creator/dashboard" className="btn btn-primary btn-lg">
                <FaRocket /> Creator Dashboard
              </Link>
            ) : isSubscribed ? (
              <>
                <div className="subscribed-badge">
                  <FaCheck /> Subscribed
                  {subscriptionEnd && (
                    <span className="subscription-end">
                      until {formatDate(subscriptionEnd)}
                    </span>
                  )}
                </div>
                <button 
                  onClick={handleUnsubscribe}
                  className="btn btn-danger btn-sm"
                >
                  Unsubscribe
                </button>
              </>
            ) : (
              <button 
                onClick={handleSubscribe}
                className="btn btn-primary btn-lg"
                disabled={!creator.subscription_price}
              >
                Subscribe {creator.subscription_price ? formatXPUNT(creator.subscription_price) : ''}
              </button>
            )}
            <div className="share-actions">
              <button onClick={handleShare} className="share-btn" title="Copy link">
                <FaCopy />
              </button>
              <button onClick={() => handleShareSocial('twitter')} className="share-btn twitter" title="Share on X">
                <FaTwitter />
              </button>
              <button onClick={() => handleShareSocial('whatsapp')} className="share-btn whatsapp" title="Share on WhatsApp">
                <FaWhatsapp />
              </button>
              <button onClick={() => handleShareSocial('telegram')} className="share-btn telegram" title="Share on Telegram">
                <FaTelegram />
              </button>
            </div>
          </div>
        </div>

        {/* Private Bets Section */}
        {isSubscribed || isOwnProfile ? (
          <div className="private-bets-section">
            <h3>
              <FaLock /> Private Bet Codes
              {isOwnProfile && <span className="owner-badge">Your Bets</span>}
            </h3>
            {privateBets.length === 0 ? (
              <div className="empty-state-small">
                <p>No private bets available yet</p>
                {isOwnProfile && (
                  <Link to="/bet-codes/create" className="btn btn-primary btn-sm">
                    <FaTicketAlt /> Create Private Bet
                  </Link>
                )}
              </div>
            ) : (
              <div className="private-bets-grid">
                {privateBets.map((bet) => (
                  <Link to={`/bet-codes/${bet.id}`} key={bet.id} className="private-bet-card">
                    <div className="bet-card-header">
                      <span className="bet-platform">{bet.platform || 'General'}</span>
                      <span className="bet-odds">Odds: {bet.odds}</span>
                    </div>
                    <h4 className="bet-title">{bet.title}</h4>
                    <div className="bet-code">{bet.code}</div>
                    <div className="bet-footer">
                      <span className="bet-games">{bet.number_of_games} games</span>
                      <span className="bet-date">{formatTimeAgo(bet.created_at)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="subscribe-to-view">
            <div className="lock-icon">🔒</div>
            <h3>Subscribe to view private bets</h3>
            <p>Get access to exclusive premium bet codes from this creator</p>
            <button 
              onClick={handleSubscribe}
              className="btn btn-primary"
              disabled={!creator.subscription_price}
            >
              Subscribe Now {creator.subscription_price ? formatXPUNT(creator.subscription_price) : ''}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatorProfile;